<template>
  <div v-if="surveyResponse" class="survey-response">
    <div class="page-content">
      <!-- survey-response title and questions -->
      <div
        v-if="!loading"
        v-for="question in surveyResponse.questions"
        :key="question.id"
        class="question-container"
      >
        <Question
          v-if="displayQuestion(question)"
          :question="question"
          :forceMobile="forceMobile"
          :context="context"
        />
      </div>
      <!-- conditional error message -->
      <div v-if="incomplete" class="question-container error-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <h4 class="tooltip-header" v-translate>Response required</h4>
          <span class="tooltip-text">
            {{ errorMessage }}
          </span>
        </div>
      </div>
      <div class="form-footer">
        <div v-if="nextStep !== ''" class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name">{{
            nextStep
          }}</translate>
        </div>
      </div>
      <!-- submission button -->
      <template v-if="!useSubmitButton">
        <md-button class="form-button" :class="{ 'dark': darkText }" @click="submitSurveyResponse">
          <span v-if="!loading"
            ><translate>{{ buttonText }}</translate></span
          >
          <span v-else>
            <LoadSpinner v-if="!darkText" color="white" />
            <LoadSpinner v-else color="black" />
          </span>
        </md-button>
      </template>
      <template v-else>
        <SubmitButton
          class="form-button-160"
          :darkText="darkText"
          :onContinue="submitSurveyResponse"
          :loading="loading"
          :buttonText="buttonText"
          :staticWidth="true"
        />
      </template>
      <!-- end of content-->
    </div>
  </div>
</template>

<script>
// state
import { mapState } from 'vuex'
// components
import LoadSpinner from '@/components/LoadSpinner'
import Question from '@/components/survey/Question'
// service models
import SurveyResponseModel from '@/services/surveyresponses'
import SurveyQuestionModel from '@/services/surveyQuestions'
// flow submit button
import SubmitButton from '@/components/requests/SubmitButton'

export default {
  name: 'SurveyResponse',
  props: {
    surveyResponse: {
      type: Object,
      default: null,
    },
    /* Array of SurveyQuestion humanReadableIds. If populated, only display questions within this list. */
    onlyDisplayQuestions: {
      type: Array,
      default: () => [],
    },
    buttonText: {
      type: String,
      default: 'Next',
    },
    nextStep: {
      type: String,
      default: '',
    },
    forceMobile: {
      type: Boolean,
      default: false,
    },
    useSubmitButton: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object,
      default: {},
    },
    darkText: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Question,
    LoadSpinner,
    SubmitButton,
  },
  data() {
    return {
      incomplete: false, // for use when validatingCompletion
      loading: false, // for use during submission
      questionsCopy: [],
    }
  },
  methods: {
    /* Returns boolean for whether or not this question should be displayed. */
    displayQuestion(question) {
      // If no specific list of questions is passed in, display every question.
      if (!this.onlyDisplayQuestions.length) {
        return true
      }
      if (this.onlyDisplayQuestions.includes(question.humanReadableId)) {
        return true
      }
      return false
    },
    validateCompletion() {
      // If partial responses are allowed, then it's incomplete
      // if each of the survey questions being displayed is empty,
      // or the question is not optional
      if (this.surveyResponse.allowPartialResponses) {
        this.incomplete = this.displayableQuestions.map(q => q.isEmpty).every(x => x)
        return
      }

      // Otherwise, if partial responses are not allowed, then it's
      // incomplete if any (some) of the questions being displayed are empty.
      this.incomplete = this.displayableQuestions.map(q => q.isEmpty).some(x => x)
    },
    /* Remove empty questions before sending to API */
    cleanSurveyResponse() {
      this.questionsCopy = [...this.surveyResponse.questions]
      this.surveyResponse.questions = this.surveyResponse.questions.filter(q => !q.isEmpty)
    },
    async submitSurveyResponse() {
      this.loading = true
      this.validateCompletion()
      if (!this.incomplete) {
        this.cleanSurveyResponse()

        // Determine if the survey is completed (some partial surveys can still have required)
        let questions = this.questionsCopy
        let isComplete = questions.map(q => !q.isEmpty || q.isOptional).every(x => x)

        await SurveyResponseModel.api
          .update(this.surveyResponse)
          .then(() => {
            this.$emit('survey-done', { success: true, complete: isComplete })
          })
          .catch(() => {
            this.$emit('survey-done', { success: false, complete: false })
          })
        return true
      }
      this.loading = false
      return false
    },
  },

  computed: {
    ...mapState(['organizationDetails', 'currentLanguage']),
    displayableQuestions() {
      return this.surveyResponse.questions.filter(q => this.displayQuestion(q))
    },
    errorMessage() {
      if (this.surveyResponse.allowPartialResponses) {
        return this.$gettext('Please fill out at least one survey field.')
      } else {
        return this.$gettext('Please fill out all survey fields.')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/grant-circles';

.survey-response {
  width: 100%;
}

.page-content {
  height: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
}

.question-container {
  width: 100%;
}

.error-message {
  color: $ruby;
  margin-bottom: 2rem;
  font-size: 0.8rem;
}

.submit-button {
  @include primary-button(var(--dark-brand-color));
}
</style>
